import { FormattedMessage, FormattedNumber } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import type CountryCode from '@alltrails/types/CountryCode';
import type Coupon from '@alltrails/types/Coupon';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import type { SubscriptionTier } from '@alltrails/types/SubscriptionTier';
import { getPriceTotal, isTrial } from '../../utils/plan';

type PaymentFinePrintTextProps = {
  billingCountry: CountryCode;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  isCouponApplied: boolean;
  isComplianceCheckboxEnabled?: boolean;
  plan: Plan;
  subscriptionTier: SubscriptionTier;
};

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=637%3A55454
function PaymentFinePrintText({
  billingCountry,
  currencyCode,
  coupon,
  isComplianceCheckboxEnabled,
  isCouponApplied,
  plan,
  subscriptionTier
}: PaymentFinePrintTextProps) {
  const showPlusTaxesText = billingCountry === 'US' || billingCountry === 'CA';
  const months = plan.numberOfMonths;
  const totalCost = <FormattedNumber key="cost" currency={currencyCode} style="currency" value={getPriceTotal(plan, coupon)} />;
  const baseAnnualCost = <FormattedNumber key="baseAnnualCost" currency={currencyCode} style="currency" value={plan.priceTotal} />;

  if (subscriptionTier === 'plus' && !isCouponApplied) {
    if (isComplianceCheckboxEnabled) {
      return (
        <FormattedMessage
          defaultMessage="I agree to be charged {cost}{plusTaxes, select, true { plus applicable taxes} other {}} for an annual subscription to Peak. If I already paid for Plus, I’ll be credited the remaining value towards my new plan. If I’m in a Plus free trial, I’ll get Peak access now and will be charged {cost} when it ends. My Peak plan renews yearly using this payment method unless I cancel in Account settings anytime before my trial ends or my renewal date. I’ll be notified of any price changes."
          values={{ cost: totalCost, plusTaxes: showPlusTaxesText }}
        />
      );
    }
    return (
      <FormattedMessage
        defaultMessage="If you paid for Plus, you’ll be charged {cost}{plusTaxes, select, true { plus applicable taxes} other {}} today and refunded the remainder of your subscription. If you’re in a Plus free trial, you’ll get Peak access now and be charged {cost}{plusTaxes, select, true { plus applicable taxes} other {}} when the trial ends. Your Peak plan will renew in {months} months unless you change your settings."
        values={{ cost: totalCost, months, plusTaxes: showPlusTaxesText }}
      />
    );
  }

  if (isTrial(plan, coupon)) {
    if (isComplianceCheckboxEnabled) {
      return (
        <FormattedMessage
          defaultMessage="I agree to be charged {cost}{plusTaxes, select, true { plus applicable taxes} other {}} after my free trial. My plan renews yearly with this payment method unless I cancel in Account settings anytime before my trial ends or my renewal date. I’ll be notified of any price changes."
          values={{ cost: totalCost, plusTaxes: showPlusTaxesText }}
        />
      );
    }

    // always multiple months here so we don't need singular form
    return (
      <FormattedMessage
        defaultMessage="You will be charged {baseAnnualCost}{plusTaxes, select, true { plus applicable taxes} other {}} when your free trial ends and your subscription will automatically renew every {months} months unless you turn off your auto-renewal within your profile settings prior to your renewal date."
        values={{ baseAnnualCost, months, plusTaxes: showPlusTaxesText }}
      />
    );
  }

  if (isCouponApplied) {
    const couponAnnualCost = coupon?.duration === 'forever' ? totalCost : baseAnnualCost;

    if (subscriptionTier === 'plus') {
      if (isComplianceCheckboxEnabled) {
        return (
          <FormattedMessage
            defaultMessage="I agree to be charged {cost}{plusTaxes, select, true { plus applicable taxes} other {}} for an annual subscription to Peak. If I already paid for Plus, I’ll be credited the remaining value towards my new plan. If I’m in a free Plus trial, I’ll get Peak access now and will be charged when it ends. My Peak plan renews yearly at {baseAnnualCost}{plusTaxes, select, true { plus applicable taxes} other {}} using this payment method unless I cancel in Account settings anytime before my trial ends or my renewal date. I’ll be notified of any price changes."
            values={{
              cost: totalCost,
              baseAnnualCost: couponAnnualCost,
              plusTaxes: showPlusTaxesText
            }}
          />
        );
      }

      return (
        <FormattedMessage
          defaultMessage="If you paid for Plus, you’ll be charged {cost}{plusTaxes, select, true { plus applicable taxes} other {}} today and refunded the remainder of your subscription. If you’re in a Plus free trial, you’ll get Peak access now and be charged {cost}{plusTaxes, select, true { plus applicable taxes} other {}} when the trial ends. Your Peak plan will automatically renew after {months} months at {baseAnnualCost}{plusTaxes, select, true { plus applicable taxes} other {}} unless you update your renewal settings."
          values={{
            cost: totalCost,
            baseAnnualCost: couponAnnualCost,
            months,
            plusTaxes: showPlusTaxesText
          }}
        />
      );
    }

    if (isComplianceCheckboxEnabled) {
      return (
        <FormattedMessage
          defaultMessage="I agree to be charged {cost}{plusTaxes, select, true { plus applicable taxes} other {}} today with yearly auto-renewal at {baseAnnualCost} using this payment method unless I cancel in Account settings anytime before my renewal date. I’ll be notified of any price changes."
          values={{
            cost: totalCost,
            baseAnnualCost: couponAnnualCost,
            plusTaxes: showPlusTaxesText
          }}
        />
      );
    }

    return (
      <FormattedMessage
        defaultMessage="You will be charged {cost}{plusTaxes, select, true { plus applicable taxes} other {}} today and automatically renew after {months} months at {baseAnnualCost} unless you update your renewal settings."
        values={{
          cost: totalCost,
          baseAnnualCost: couponAnnualCost,
          months,
          plusTaxes: showPlusTaxesText
        }}
      />
    );
  }

  if (isComplianceCheckboxEnabled) {
    return (
      <FormattedMessage
        defaultMessage="I agree to be charged {cost}{plusTaxes, select, true { plus applicable taxes} other {}} today with yearly auto-renewal with this payment method unless I cancel in Account settings anytime before my renewal date. I’ll be notified of any price changes."
        values={{ cost: totalCost, plusTaxes: showPlusTaxesText }}
      />
    );
  }

  return (
    <FormattedMessage
      defaultMessage="You will be charged {cost}{plusTaxes, select, true { plus applicable taxes} other {}} today and automatically renew after {months} months unless you update your renewal settings."
      values={{ cost: totalCost, months, plusTaxes: showPlusTaxesText }}
    />
  );
}

type Props = {
  billingCountry: CountryCode;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  isCouponApplied: boolean;
  isComplianceCheckboxEnabled?: boolean;
  plan: Plan;
  subscriptionTier: SubscriptionTier;
};

export default function PaymentFinePrint({
  billingCountry,
  currencyCode,
  coupon,
  isComplianceCheckboxEnabled = false,
  isCouponApplied,
  plan,
  subscriptionTier
}: Props) {
  return (
    <Typography color={isComplianceCheckboxEnabled ? 'primary' : 'secondary'} variant="paragraph100">
      <PaymentFinePrintText
        billingCountry={billingCountry}
        coupon={coupon}
        currencyCode={currencyCode}
        isCouponApplied={isCouponApplied}
        isComplianceCheckboxEnabled={isComplianceCheckboxEnabled}
        plan={plan}
        subscriptionTier={subscriptionTier}
      />
    </Typography>
  );
}
